/**
 * Curso del centro de ayuda (/ayuda). Único archivo de datos: módulos → lecciones.
 *
 * Reglas:
 *  - Cada lección es UNA tarea que se muestra en menos de ~1:20.
 *  - Los pasos nombran botones, menús, pestañas y campos TAL CUAL aparecen en la interfaz
 *    (sacados de src/pages, src/components y src/layouts/AdminLayout.tsx). Si cambia un texto
 *    en la app, hay que cambiarlo acá.
 *  - plan: 'pago' marca lo que en el plan Gratis aparece con candado (FREE_PLAN_LOCKED en
 *    AdminLayout.tsx y FreePlan.LockedApiPrefixes en el backend).
 *  - video: se completa en la fase 2 (duración en segundos y fecha de publicación). El mp4 y
 *    el póster van en public/ayuda/<slug>.mp4 y public/ayuda/<slug>.jpg.
 *  - pdf: true solo cuando exista public/ayuda/<slug>.pdf.
 *  - Si sumás o sacás una lección, actualizá public/sitemap.xml.
 */

export interface Leccion {
  slug: string;
  titulo: string;
  descripcion: string;
  sinonimos: string[];
  pasos: string[];
  video?: { duracion: number; fecha?: string };
  pdf?: boolean;
  plan?: 'pago';
}

export interface Modulo {
  id: string;
  nombre: string;
  bajada: string;
  lecciones: Leccion[];
}

export const MODULOS: Modulo[] = [
  {
    id: 'primeros-pasos',
    nombre: 'Primeros pasos',
    bajada: 'Crear la cuenta, entrar y dejar listo tu negocio.',
    lecciones: [
      {
        slug: 'como-crear-tu-cuenta',
        titulo: 'Cómo crear tu cuenta',
        descripcion: 'Abrir tu cuenta de DashFlow con tu email o con Google, gratis y sin tarjeta.',
        sinonimos: ['registrarme', 'darme de alta', 'abrir cuenta', 'empezar gratis', 'probar DashFlow'],
        pasos: [
          'Entrá a dashflow.com.ar y tocá "Empezar gratis".',
          'Si querés entrar con Google, tildá "Acepto los términos y condiciones" y tocá el botón de Google. Después escribí el "Nombre del negocio" y tocá "Continuar →".',
          'Si preferís email, completá "Nombre del negocio", "Tu email" y "Tu contraseña" (mínimo 6 caracteres).',
          'Tildá "Acepto los términos y condiciones".',
          'Tocá "Crear cuenta". Te lleva directo al asistente para completar tu perfil.',
        ],
      },
      {
        slug: 'como-entrar-con-tu-whatsapp',
        titulo: 'Cómo entrar con tu WhatsApp',
        descripcion: 'Crear la cuenta o entrar sin contraseña, con un código que te llega por WhatsApp.',
        sinonimos: ['entrar con el celular', 'código por WhatsApp', 'login sin contraseña', 'registrarme con WhatsApp'],
        pasos: [
          'En la pantalla "Entrá con tu WhatsApp", escribí tu número en "WhatsApp": con código de área, sin el 0 ni el 15.',
          'Tocá "Enviar código".',
          'Revisá tu WhatsApp, copiá el código de 6 números y escribilo en "Código".',
          'Tocá "Verificar y entrar". Si es una cuenta nueva, sigue el asistente para completar tu perfil.',
          'Si no te llegó, tocá "Reenviar código". Si escribiste mal el número, tocá "Cambiar número".',
        ],
      },
      {
        slug: 'como-completar-tu-perfil',
        titulo: 'Cómo completar tu perfil y personalizar tu marca',
        descripcion: 'Los 5 pasos del asistente inicial: tu nombre, tu tienda, tu logo y tus colores.',
        sinonimos: ['configuración inicial', 'primeros pasos', 'cargar logo', 'colores de la marca', 'datos de mi negocio'],
        pasos: [
          'En "Lo primero, saludarnos", escribí "Tu nombre" y tocá "Siguiente »".',
          'En "Contanos de tu tienda", elegí "¿Qué vendés?", "¿Cuántos SKUs manejás?" y "¿En qué canales vendés hoy?".',
          'En "Personalizá tu marca", tocá "Subir tu logo" y elegí una de las paletas en "Los colores de tu app" (o "Crear mi propia paleta").',
          'En "Queremos tener un detalle", completá "Tu cumpleaños" y "Teléfono". Instagram y web son opcionales.',
          'Tocá "Ir al panel". Listo: ya estás adentro.',
        ],
      },
      {
        slug: 'como-recuperar-tu-contrasena',
        titulo: 'Cómo entrar y recuperar tu contraseña',
        descripcion: 'Iniciar sesión con email y contraseña, y pedir una nueva si te la olvidaste.',
        sinonimos: ['entrar', 'ingresar', 'olvidé la contraseña', 'recuperar clave', 'resetear contraseña'],
        pasos: [
          'En dashflow.com.ar tocá "Ingresar".',
          'Escribí "Tu email" y "Tu contraseña" y tocá "Iniciar sesión".',
          'Si no te acordás la contraseña, tocá "¿Olvidaste tu contraseña?".',
          'Escribí "Tu email" y tocá "Enviar enlace".',
          'Abrí el mail y seguí el enlace para crear una nueva. Vence en 1 hora; si no lo ves, mirá en spam.',
        ],
      },
      {
        slug: 'como-leer-el-dashboard',
        titulo: 'Cómo leer el Dashboard',
        descripcion: 'Qué te muestra la pantalla de inicio: inventario valorizado, stock crítico, ventas y alertas.',
        sinonimos: ['tablero', 'panel principal', 'inicio', 'resumen del negocio', 'indicadores'],
        pasos: [
          'En el menú, "Inicio" → "Dashboard".',
          'Arriba tenés "Inventario Valorizado", "Stocks Críticos" y "Profit neto (período)".',
          'En "Período:" elegí "Día", "Semana", "Mes" o "Custom" para ver "Ventas", "Unidades vendidas", "Órdenes" y "Ticket promedio".',
          'Más abajo, "Alertas de stock" te lista lo que se está terminando.',
          'Si querés llevarte los números, tocá "Exportar métricas".',
        ],
      },
      {
        slug: 'como-cargar-los-datos-de-tu-empresa',
        titulo: 'Cómo cambiar el nombre y los colores de tu empresa',
        descripcion: 'Editar el nombre y los colores de tu cuenta cuando quieras.',
        sinonimos: ['datos de la empresa', 'nombre del negocio', 'cambiar colores', 'personalizar', 'configuración general'],
        pasos: [
          'En el menú, "Sistema" → "Configuración".',
          'En la tarjeta "Datos del tenant", cambiá el "Nombre".',
          'Si querés, cambiá "Color primario" y "Color secundario".',
          'Tocá "Guardar". Vas a ver "Guardado en servidor".',
        ],
      },
    ],
  },
  {
    id: 'productos',
    nombre: 'Productos',
    bajada: 'Tu catálogo: productos, categorías, talles y precios.',
    lecciones: [
      {
        slug: 'como-cargar-un-producto',
        titulo: 'Cómo cargar un producto',
        descripcion: 'Dar de alta un producto con su precio, su costo y su stock.',
        sinonimos: ['dar de alta un producto', 'agregar artículo', 'crear producto', 'cargar mercadería', 'nuevo artículo'],
        pasos: [
          'En el menú, "Catálogo" → "Productos", y tocá "Nuevo".',
          'Completá "Nombre" y "SKU" (son obligatorios). Si tenés lector, cargá el "Código de barras".',
          'Elegí la "Categoría".',
          'Cargá "PVP" (precio de venta) y "Costo".',
          'Cargá "Stock actual", "Stock mínimo" y "Punto de reposición".',
          'Elegí la "Unidad de medida" y dejá tildados "Activo" y "Controlar stock".',
          'Tocá "Crear". El producto aparece en la lista. En el plan Gratis podés tener hasta 100 productos activos.',
        ],
      },
      {
        slug: 'como-crear-categorias',
        titulo: 'Cómo crear categorías',
        descripcion: 'Agrupar tus productos en categorías (rubros) para encontrarlos y ordenarlos.',
        sinonimos: ['rubros', 'familias de productos', 'agrupar productos', 'clasificar artículos', 'secciones'],
        pasos: [
          'En "Catálogo" → "Productos", tocá "Nuevo" (o "Editar" en un producto).',
          'Al lado de "Categoría", tocá el engranaje "Gestionar categorías".',
          'Escribí el nombre en "Nueva categoría" y tocá "Crear".',
          'Para renombrar o cambiar el orden, editá la fila y guardala con el ícono del disquete.',
          'Tocá "Cerrar". La categoría nueva ya aparece en "Categoría".',
        ],
      },
      {
        slug: 'como-cargar-talles-y-colores',
        titulo: 'Cómo cargar talles y colores (variantes)',
        descripcion: 'Manejar el stock de cada talle, color o presentación de un mismo producto.',
        sinonimos: ['talles', 'colores', 'variantes', 'curva de talles', 'stock por talle', 'presentaciones'],
        pasos: [
          'Abrí el producto con "Nuevo" o "Editar".',
          'Bajá hasta "Variantes / Talles" y tocá "Agregar variante".',
          'En cada fila completá "Variante" (por ejemplo "talle 42"), "Código de barras" y "Stock".',
          'Abajo vas a ver "Stock total": el "Stock actual" se calcula solo como la suma de las variantes.',
          'Tocá "Crear" o "Guardar". El precio es el mismo para todas las variantes.',
        ],
      },
      {
        slug: 'como-importar-productos-desde-excel',
        titulo: 'Cómo importar productos desde Excel',
        descripcion: 'Cargar todo tu catálogo de una vez con un archivo CSV.',
        sinonimos: ['subir planilla', 'carga masiva', 'importar Excel', 'importar lista de productos', 'migrar catálogo'],
        pasos: [
          'En "Catálogo" → "Productos", tocá "Importar".',
          'Mirá los encabezados que pide el cuadro "Importar productos desde CSV" y armá tu planilla con esas columnas (name, sku, price, cost, currentStock…).',
          'En Excel, guardala como CSV.',
          'Tocá "Seleccionar archivo CSV" y elegí el archivo (o pegá el texto en "o pegar CSV aquí").',
          'Tocá "Importar". Vas a ver cuántos quedaron "Creados", "Actualizados" y "Omitidos". Si el SKU ya existía, el producto se actualiza.',
          'Tocá "Cerrar".',
        ],
      },
      {
        slug: 'como-actualizar-precios-en-masa',
        titulo: 'Cómo actualizar los precios de todos tus productos',
        descripcion: 'Bajar el catálogo, cambiar precios y costos en Excel y volver a subirlo.',
        sinonimos: ['aumentar precios', 'remarcar', 'actualizar lista de precios', 'subir precios', 'cambiar precios de todos'],
        pasos: [
          'En "Catálogo" → "Productos", tocá "Exportar". Se baja el archivo products.csv.',
          'Abrilo en Excel y cambiá las columnas price (precio) y cost (costo).',
          'No borres la columna currentStock: si falta, el stock queda en 0.',
          'Guardalo como CSV.',
          'Tocá "Importar", después "Seleccionar archivo CSV" y "Importar". Tienen que aparecer como "Actualizados".',
        ],
      },
      {
        slug: 'como-calcular-tu-margen',
        titulo: 'Cómo calcular tu margen',
        descripcion: 'Simular precio de venta y margen de un producto, también para Mercado Libre.',
        sinonimos: ['margen de ganancia', 'calcular precio', 'cuánto gano', 'comisión de Mercado Libre', 'simular precio'],
        pasos: [
          'En el menú, "Catálogo" → "Precios".',
          'En "Producto", elegí el que querés analizar.',
          'En "Costos" ves lo que te cuesta; en "Venta público", el "PVP" y el "Margen".',
          'Probá otro precio en "PVP simulado" y mirá cómo cambia el "Margen simulado".',
          'En "Mercado Libre", cargá la "Comisión ML %" para ver el "Neto ML". Esta pantalla simula: no cambia el precio del producto.',
        ],
      },
    ],
  },
  {
    id: 'stock',
    nombre: 'Stock',
    bajada: 'Ajustes, depósitos y qué reponer.',
    lecciones: [
      {
        slug: 'como-ajustar-el-stock',
        titulo: 'Cómo ajustar el stock',
        descripcion: 'Corregir el stock después de contar, por una rotura o para la carga inicial.',
        sinonimos: ['corregir stock', 'inventario físico', 'conteo', 'dar de baja mercadería', 'cargar stock inicial'],
        pasos: [
          'En el menú, "Operaciones" → "Inventario".',
          'Dejá marcados "Productos" y "Por producto". Si querés, buscá el producto.',
          'En la fila, tocá el lápiz "Ajustar stock".',
          'En "Ajuste de stock" escribí el "Nuevo stock".',
          'Elegí el "Motivo": "Carga inicial", "Ajuste por corrección", "Mercadería deteriorada", "Merma de producción" u "Otro".',
          'Si querés, sumá "Notas" y tocá "Guardar". El ajuste queda registrado con tu usuario y el stock antes y después.',
        ],
      },
      {
        slug: 'como-crear-depositos',
        titulo: 'Cómo crear depósitos o sucursales',
        descripcion: 'Dar de alta otro depósito o local para llevar el stock de cada lugar.',
        sinonimos: ['sucursales', 'almacenes', 'otro local', 'stock por depósito', 'depósito nuevo'],
        pasos: [
          'En "Operaciones" → "Inventario", tocá "Depósitos".',
          'En "Depósitos / Almacenes" completá "Nombre" y elegí el "Tipo" (por ejemplo "Interno / Propio").',
          'Si querés, sumá "Código" y "Dirección".',
          'Tocá "Crear".',
          'Para ver el stock de cada lugar, tocá "Por almacén".',
        ],
      },
      {
        slug: 'como-pasar-stock-entre-depositos',
        titulo: 'Cómo pasar stock entre depósitos',
        descripcion: 'Mover mercadería de un depósito o local a otro sin perder el rastro.',
        sinonimos: ['transferir stock', 'pasar mercadería', 'mover stock', 'transferencia entre sucursales'],
        pasos: [
          'En "Operaciones" → "Inventario", buscá el producto.',
          'En la fila, tocá el camión "Transferencia interna".',
          'Elegí "Desde" y "Hacia".',
          'Escribí la "Cantidad" y tocá "Transferir".',
        ],
      },
      {
        slug: 'como-ver-el-valor-de-tu-inventario',
        titulo: 'Cómo ver cuánto vale tu inventario',
        descripcion: 'El valor de todo tu stock a precio de costo o de venta, y los lotes con vencimiento.',
        sinonimos: ['valor del stock', 'inventario valorizado', 'cuánta plata tengo en mercadería', 'lotes', 'vencimientos'],
        pasos: [
          'En el menú, "Operaciones" → "Inventario".',
          'Mirá la tarjeta "Valor total del inventario".',
          'En "Valorizado a:" elegí "Costo" o "Venta".',
          'Si trabajás con lotes, tocá la flecha "Ver lotes con stock" en un producto para ver "Lote", "Cantidad disponible" y "Vencimiento".',
        ],
      },
      {
        slug: 'como-saber-que-reponer',
        titulo: 'Cómo saber qué reponer',
        descripcion: 'La lista de productos que llegaron al punto de reposición, con la cantidad sugerida.',
        sinonimos: ['qué comprar', 'faltantes', 'stock bajo', 'productos por agotarse', 'alertas de stock'],
        plan: 'pago',
        pasos: [
          'En el menú, "Operaciones" → "Reabastecimiento".',
          'Tildá "Solo alertas" para ver únicamente lo que hay que reponer.',
          'Si querés, filtrá por "Proveedor".',
          'Cada producto dice "CRÍTICO", "ALERTA" u "OK", con su "Stock", el "Reorden" y la cantidad "Sugerido".',
          'Cambiá a "Tabla" para verlo en filas y tocá "Exportar CSV" para mandárselo a tu proveedor.',
        ],
      },
    ],
  },
  {
    id: 'vender-y-caja',
    nombre: 'Vender y caja',
    bajada: 'El mostrador: abrir la caja, cobrar y cerrar el día.',
    lecciones: [
      {
        slug: 'como-abrir-la-caja',
        titulo: 'Cómo abrir la caja',
        descripcion: 'Arrancar el día con el efectivo inicial para poder vender en el Punto de Venta.',
        sinonimos: ['abrir el turno', 'iniciar caja', 'fondo de caja', 'cambio inicial', 'arrancar el día'],
        pasos: [
          'En el menú, "Comercial" → "Caja".',
          'En la tarjeta "Abrir caja", escribí el "Monto inicial" de efectivo.',
          'Tocá "Abrir caja".',
          'La caja queda "Abierta" y ves "Apertura", "Ventas" y "Efectivo esperado".',
        ],
      },
      {
        slug: 'como-hacer-una-venta',
        titulo: 'Cómo hacer una venta en el Punto de Venta',
        descripcion: 'Cobrar en el mostrador con el lector de código de barras o buscando el producto.',
        sinonimos: ['cobrar en mostrador', 'vender', 'lector de código de barras', 'ticket', 'POS', 'caja registradora'],
        pasos: [
          'En el menú, "Comercial" → "Punto de Venta". Si la caja está cerrada, tocá "Abrir caja" primero.',
          'Escaneá el código de barras o escribí el nombre o SKU en el buscador y apretá Enter.',
          'Si el producto tiene talles o colores, elegí la variante.',
          'Ajustá las cantidades con + y −.',
          'Si querés, elegí el "Cliente" (si no, queda como consumidor final).',
          'En "Método de pago" elegí "Efectivo", "Tarjeta", "Transferencia" o "QR". En efectivo, cargá "Paga con" o tocá "Justo" para ver el "Vuelto".',
          'Tocá "Cobrar". En "Venta registrada" podés tocar "Imprimir ticket" o "Nueva venta".',
        ],
      },
      {
        slug: 'como-cobrar-con-varios-medios-de-pago',
        titulo: 'Cómo cobrar con varios medios de pago',
        descripcion: 'Una parte en efectivo y otra con tarjeta, transferencia o QR en la misma venta.',
        sinonimos: ['pago mixto', 'pago dividido', 'parte efectivo parte tarjeta', 'combinar medios de pago'],
        pasos: [
          'Con los productos cargados en el Punto de Venta, activá "Dividir".',
          'Escribí cuánto se paga con cada medio. Con "Resto" completás lo que falta en ese medio.',
          'Mientras no cierre, vas a ver "Falta asignar" o "Te pasaste por".',
          'Si hay una parte en efectivo, cargá "Paga con" para ver el "Vuelto".',
          'Tocá "Cobrar". La venta registrada muestra cada medio con su monto.',
        ],
      },
      {
        slug: 'como-aplicar-un-descuento',
        titulo: 'Cómo aplicar un descuento o un recargo',
        descripcion: 'Hacer un descuento o cobrar un recargo, en porcentaje o en monto fijo.',
        sinonimos: ['hacer descuento', 'rebaja', 'bonificación', 'recargo con tarjeta', 'promo'],
        pasos: [
          'En el Punto de Venta, en "Cobro", tocá "Gestionar Extras".',
          'Elegí "Descuento" o "Recargo".',
          'Elegí "Porcentaje" o "Monto fijo" y escribí el valor.',
          'Revisá el "Total:" y tocá "Aplicar".',
          'Para sacarlo, volvé a "Gestionar Extras" y tocá "Quitar".',
        ],
      },
      {
        slug: 'como-registrar-un-retiro-de-caja',
        titulo: 'Cómo registrar un ingreso o un retiro de efectivo',
        descripcion: 'Anotar la plata que entra o sale de la caja fuera de las ventas.',
        sinonimos: ['retiro de caja', 'sacar plata de la caja', 'gasto de caja', 'entrada de dinero', 'movimiento de caja'],
        pasos: [
          'En "Comercial" → "Caja", con la caja abierta, tocá "Ingreso" o "Egreso".',
          'Escribí el "Monto" y elegí el "Método".',
          'En "Descripción" contá de qué se trata (por ejemplo, "Pago proveedor de bolsas").',
          'Tocá "Registrar". El movimiento aparece en la tabla "Movimientos".',
        ],
      },
      {
        slug: 'como-cerrar-la-caja',
        titulo: 'Cómo cerrar la caja',
        descripcion: 'Hacer el arqueo: contar el efectivo y ver si sobra o falta.',
        sinonimos: ['arqueo de caja', 'cierre de turno', 'cerrar el día', 'cuadrar la caja', 'rendición de caja'],
        pasos: [
          'En "Comercial" → "Caja", tocá "Cerrar caja".',
          'Mirá el "Efectivo esperado" y contá el efectivo que tenés.',
          'Escribilo en "Efectivo contado (declarado)". Abajo se calcula la "Diferencia:".',
          'Tocá "Cerrar caja". Vas a ver si quedó sin diferencia, con sobrante o con faltante.',
        ],
      },
      {
        slug: 'como-buscar-y-eliminar-una-venta',
        titulo: 'Cómo buscar, editar o eliminar una venta',
        descripcion: 'Encontrar una venta, ver su detalle y corregirla o borrarla.',
        sinonimos: ['anular venta', 'borrar venta', 'cancelar venta', 'historial de ventas', 'buscar una venta'],
        pasos: [
          'En el menú, "Comercial" → "Ventas".',
          'Buscá en "Buscar por cliente, nombre, referencia..." o usá las pestañas "Todas" y "Pendiente de Cobro".',
          'Arriba tenés los totales "Facturado", "Cobrado" y "Pendiente".',
          'Tocá la venta para abrir el "Detalle de Venta".',
          'Para cambiarla, tocá "Editar". Si ya descontó stock, primero tocá "Revertir entrega".',
          'Para borrarla, tocá "Eliminar venta" al final del detalle y confirmá. Si fue entregada, el stock vuelve.',
        ],
      },
    ],
  },
  {
    id: 'clientes-y-cobros',
    nombre: 'Clientes y cobros',
    bajada: 'Tus clientes, lo que te deben y cómo cobrarles.',
    lecciones: [
      {
        slug: 'como-cargar-un-cliente',
        titulo: 'Cómo cargar un cliente',
        descripcion: 'Dar de alta un cliente con sus datos de contacto y su límite de crédito.',
        sinonimos: ['alta de cliente', 'agregar cliente', 'nuevo cliente', 'ficha de cliente', 'datos del cliente'],
        pasos: [
          'En el menú, "Comercial" → "Clientes", y tocá "Nuevo".',
          'Completá "Nombre legal" (es obligatorio).',
          'Sumá "CUIT", "Email", "Teléfono" y "WhatsApp".',
          'Si le vendés a cuenta, cargá el "Límite de crédito".',
          'Tocá "Guardar".',
        ],
      },
      {
        slug: 'como-registrar-un-pago',
        titulo: 'Cómo registrar el pago de una venta',
        descripcion: 'Anotar un pago total o parcial de una venta que quedó pendiente.',
        sinonimos: ['cargar un pago', 'pago parcial', 'abono', 'seña', 'cobrar saldo'],
        pasos: [
          'En "Comercial" → "Ventas", abrí la venta.',
          'Tocá "Registrar pago". Vas a ver "Total:", "Abonado:" y "Saldo:".',
          'En "Monto del pago" ya viene el saldo: cambialo si te pagan una parte.',
          'Elegí el "Método de pago" y la "Fecha".',
          'Si querés, cargá la "Referencia" (número de transferencia o de cheque) y adjuntá el comprobante.',
          'Tocá "Registrar pago".',
        ],
      },
      {
        slug: 'como-cobrarle-a-un-cliente',
        titulo: 'Cómo ver lo que te debe un cliente y cobrarle',
        descripcion: 'La cuenta corriente del cliente: saldo, vencido y registrar lo que te pagó.',
        sinonimos: ['fiado', 'cuenta corriente', 'cobrar deuda', 'saldo del cliente', 'vender a crédito'],
        pasos: [
          'En "Comercial" → "Clientes", tocá el ícono "Cuenta Corriente" del cliente.',
          'Arriba ves "Saldo Actual", "Límite de Crédito", "Crédito Disponible" y "Vencido".',
          'En "Órdenes Pendientes" están las ventas que debe; en "Pagos", lo que ya pagó.',
          'Para cobrar, entrá a "Registrar Pago" y completá "Monto", "Método de pago" y "Referencia".',
          'Tocá "Registrar Pago". El pago se aplica solo a las ventas más viejas.',
        ],
      },
    ],
  },
  {
    id: 'facturacion',
    nombre: 'Facturación electrónica',
    bajada: 'Conectar ARCA y emitir facturas desde DashFlow.',
    lecciones: [
      {
        slug: 'como-conectar-arca',
        titulo: 'Cómo conectar ARCA para facturar',
        descripcion: 'Dar de alta el punto de venta, delegar el servicio y verificar la conexión.',
        sinonimos: ['facturación electrónica', 'AFIP', 'ARCA', 'factura electrónica', 'punto de venta', 'CUIT'],
        plan: 'pago',
        pasos: [
          'En el menú, "Sistema" → "Configuración", y bajá hasta "Facturación electrónica (ARCA)".',
          'Si te ayuda tu contador, tocá "Copiar link para mi contador" y mandáselo. También podés abrir "Ver instructivo paso a paso con capturas".',
          'Paso 1: en ARCA, dá de alta un punto de venta "Web Service". El botón "Abrir ARCA" te lleva.',
          'Paso 2: delegá el servicio "Facturación Electrónica" al CUIT que aparece en "CUIT representante" (tocá "Copiar"). Nosotros aceptamos la delegación en el día hábil.',
          'Paso 3: completá "CUIT", "Punto de venta", "Condición frente al IVA", "Razón social (fiscal)" y "Domicilio fiscal", y tocá "Guardar".',
          'Tocá "Verificar conexión". Cuando sale bien, la tarjeta dice "Conectado".',
        ],
      },
      {
        slug: 'como-facturar-en-el-punto-de-venta',
        titulo: 'Cómo facturar al vender en el Punto de Venta',
        descripcion: 'Emitir la factura electrónica en el mismo momento del cobro.',
        sinonimos: ['factura electrónica', 'sacar factura', 'CAE', 'factura B', 'facturar con ARCA'],
        plan: 'pago',
        pasos: [
          'En el Punto de Venta, cargá los productos como siempre.',
          'En "Cobro", activá "Facturar (CAE)".',
          'Elegí el "Comprobante": "Factura A", "Factura B" o "Factura C".',
          'En "Documento" elegí "Consumidor Final", "CUIT" o "DNI" y cargá el "N° doc" si hace falta.',
          'Si querés mandarla por mail, completá "Email del cliente (opcional)".',
          'Tocá "Cobrar". La venta registrada muestra el número de comprobante y el CAE.',
        ],
      },
      {
        slug: 'como-facturar-ventas-ya-hechas',
        titulo: 'Cómo facturar una o varias ventas ya hechas',
        descripcion: 'Facturar después una venta, o juntar varias ventas de un mismo cliente en una factura.',
        sinonimos: ['facturar después', 'factura agrupada', 'ventas sin facturar', 'emitir comprobante'],
        plan: 'pago',
        pasos: [
          'En "Comercial" → "Ventas", tocá el ícono "Facturar (CAE)" de la venta.',
          'Para juntar varias del mismo cliente, tildalas y tocá "Facturar N ventas".',
          'Revisá "Neto", "IVA" y "Total".',
          'Elegí "Tipo de comprobante", "Tipo de documento", "N° de documento" y "Condición IVA del receptor".',
          'Tocá "Emitir comprobante". Cuando sale "Comprobante autorizado", tocá "Descargar PDF".',
        ],
      },
    ],
  },
  {
    id: 'compras-y-gastos',
    nombre: 'Compras y gastos',
    bajada: 'Proveedores, compras que suman stock, gastos y flujo de caja.',
    lecciones: [
      {
        slug: 'como-cargar-un-proveedor',
        titulo: 'Cómo cargar un proveedor',
        descripcion: 'Dar de alta un proveedor con sus datos y su plazo de pago.',
        sinonimos: ['alta de proveedor', 'agregar proveedor', 'nuevo proveedor', 'cargar distribuidor', 'mayorista'],
        plan: 'pago',
        pasos: [
          'En el menú, "Abastecimiento" → "Proveedores", y tocá "Nuevo proveedor".',
          'Completá "Nombre" (es obligatorio) y "CUIT".',
          'Sumá "Email", "Teléfono" y "Ciudad".',
          'En "Plazo pago (días)" poné cuántos días te da para pagar.',
          'Tocá "Crear".',
        ],
      },
      {
        slug: 'como-registrar-una-compra',
        titulo: 'Cómo registrar una compra',
        descripcion: 'Armar la orden de compra a tu proveedor con productos, cantidades y costos.',
        sinonimos: ['cargar compra', 'orden de compra', 'pedido al proveedor', 'comprar mercadería', 'OC'],
        plan: 'pago',
        pasos: [
          'En el menú, "Abastecimiento" → "Compras", y tocá "Nueva compra".',
          'Completá "Referencia", elegí el "Proveedor" y la "Fecha esperada".',
          'Elegí la "Condición de pago". Si no es "Contado", aparece la "Fecha de vencimiento".',
          'En "Items" tocá "Agregar fila", elegí el "Producto" y cargá "Cantidad", "Costo unitario" e "IVA %".',
          'Revisá el "Total:" y tocá "Crear".',
          'Para mandársela al proveedor, tocá el ícono de copiar (la copia como texto para WhatsApp) o el de PDF.',
        ],
      },
      {
        slug: 'como-recibir-la-mercaderia',
        titulo: 'Cómo recibir la mercadería y sumar el stock',
        descripcion: 'Cuando llega el pedido, cargar lo que vino para que se sume al stock.',
        sinonimos: ['ingresar mercadería', 'recepción de compra', 'llegó el pedido', 'sumar stock', 'remito'],
        plan: 'pago',
        pasos: [
          'En "Abastecimiento" → "Compras", tocá "Recibir" en la orden.',
          'Cada ítem muestra lo pedido, lo ya recibido y lo pendiente.',
          'Escribí la "Cantidad" que llegó. Si vino en lotes, cargá "Lote" y "Vencimiento" y usá "Agregar otro lote".',
          'Tocá "Confirmar recepción". La orden pasa a "Recibida" (o "Parcial") y el stock se suma.',
        ],
      },
      {
        slug: 'como-pagarle-a-un-proveedor',
        titulo: 'Cómo pagarle a un proveedor y qué pagar primero',
        descripcion: 'Registrar el pago de una compra y ver tus deudas ordenadas por vencimiento.',
        sinonimos: ['pagar proveedor', 'orden de pago', 'deudas con proveedores', 'vencimientos', 'cuentas a pagar'],
        plan: 'pago',
        pasos: [
          'En "Abastecimiento" → "Compras", tocá "Registrar pago" en la orden.',
          'Completá "Monto del pago", "Método de pago" y "Fecha", y tocá "Registrar pago".',
          'Para ver todo lo que debés, entrá a "Abastecimiento" → "Orden de pago".',
          'Arriba tenés "Total adeudado", "Vencido" y "Vence en 30 días". La lista ya viene ordenada: vencidas primero.',
        ],
      },
      {
        slug: 'como-cargar-un-gasto',
        titulo: 'Cómo cargar un gasto',
        descripcion: 'Anotar un gasto del día a día, pagado o como deuda a pagar.',
        sinonimos: ['registrar gasto', 'anotar gasto', 'egreso', 'gasto del día', 'factura a pagar'],
        plan: 'pago',
        pasos: [
          'En el menú, "Inicio" → "Gastos", y tocá "Cargar gasto".',
          'Elegí "Ya está pagado" o "A pagar (deuda)".',
          'Completá "Descripción", "Monto", "Fecha" y "Categoría".',
          'Si es una deuda, elegí el "Proveedor" y cuándo "Vence".',
          'Tocá "Guardar". Aparece en "Del día a día" y en el Flujo de Caja.',
        ],
      },
      {
        slug: 'como-cargar-tus-gastos-fijos',
        titulo: 'Cómo cargar tus gastos fijos',
        descripcion: 'Alquiler, contador, internet: lo que pagás todos los meses.',
        sinonimos: ['gastos fijos', 'alquiler', 'costos mensuales', 'gastos de estructura', 'gastos mensuales'],
        plan: 'pago',
        pasos: [
          'En "Inicio" → "Gastos", entrá a la pestaña "Fijos".',
          'Completá "Nombre", "Categoría", "Monto mensual" y "Vence el día".',
          'Tocá "Agregar".',
          'Los vencimientos aparecen en el Calendario y en el Flujo de Caja.',
        ],
      },
      {
        slug: 'como-ver-el-flujo-de-caja',
        titulo: 'Cómo ver el flujo de caja',
        descripcion: 'La plata que entra y sale en un período, con lo que falta cobrar.',
        sinonimos: ['flujo de caja', 'cash flow', 'ingresos y egresos', 'saldo', 'proyección de cobros'],
        plan: 'pago',
        pasos: [
          'En el menú, "Inicio" → "Flujo de Caja".',
          'Elegí "Desde" y "Hasta" y tocá "Consultar".',
          'Arriba tenés "Ingresos", "Egresos" y "Balance Neto".',
          'El gráfico "Saldo Acumulado" muestra cómo evoluciona la plata día a día.',
          'Abajo están las tablas de ingresos y egresos. Tocá un cobro para ver la venta.',
        ],
      },
    ],
  },
  {
    id: 'canales-reportes-equipo',
    nombre: 'Canales, reportes y equipo',
    bajada: 'Mercado Libre, Tienda Nube, lo que más vendés y tu equipo.',
    lecciones: [
      {
        slug: 'como-conectar-mercado-libre',
        titulo: 'Cómo conectar Mercado Libre',
        descripcion: 'Vincular tu cuenta de Mercado Libre para sincronizar stock y órdenes.',
        sinonimos: ['vincular Mercado Libre', 'integrar ML', 'conectar cuenta de ML', 'sincronizar Mercado Libre', 'MELI'],
        plan: 'pago',
        pasos: [
          'En el menú, "Sistema" → "Canales".',
          'En la tarjeta de MercadoLibre, tocá "Conectar MercadoLibre".',
          'Entrá con tu cuenta de Mercado Libre y autorizá a DashFlow.',
          'De vuelta en DashFlow, la tarjeta dice "Conectado" y "Cuenta conectada".',
          'Tocá "Sincronizar". Solo se sincronizan los productos vinculados (lo ves en la próxima lección).',
        ],
      },
      {
        slug: 'como-vincular-publicaciones-de-mercado-libre',
        titulo: 'Cómo vincular tus publicaciones de Mercado Libre',
        descripcion: 'Unir cada publicación con un producto de tu catálogo para que el stock sea uno solo.',
        sinonimos: ['mapear publicaciones', 'vincular productos ML', 'asociar SKU', 'mapeo Mercado Libre'],
        plan: 'pago',
        pasos: [
          'En el menú, "Sistema" → "Mapeo ML".',
          'Si tenés muchas publicaciones, tocá "Cargar todas".',
          'En cada publicación, buscá tu producto en "Buscar producto..." o tocá una de las "Sugerencias:".',
          'Si es un kit o combo, cambiá la "Cant.". La fila pasa de "Sin mapear" a "Mapeado".',
          'Tocá "Guardar todos los mapeos".',
        ],
      },
      {
        slug: 'como-conectar-tienda-nube',
        titulo: 'Cómo conectar Tienda Nube',
        descripcion: 'Vincular tu tienda online para que el stock se descuente en los dos lados.',
        sinonimos: ['conectar Tiendanube', 'integrar tienda online', 'vincular mi tienda', 'sincronizar Tienda Nube'],
        plan: 'pago',
        pasos: [
          'En el menú, "Sistema" → "Canales".',
          'En la tarjeta de Tienda Nube, tocá "Conectar TiendaNube".',
          'Entrá con tu cuenta de Tienda Nube y aceptá los permisos.',
          'De vuelta en DashFlow, la tarjeta muestra "Cuenta conectada" y el "Store ID:".',
          'Tocá "Sincronizar".',
        ],
      },
      {
        slug: 'como-ver-tus-productos-mas-vendidos',
        titulo: 'Cómo ver tus productos más vendidos',
        descripcion: 'El ranking de lo que más vendés por semana, mes, trimestre o año, y la comparación con el año pasado.',
        sinonimos: ['reporte de ventas', 'más vendidos', 'ranking de productos', 'comparar con el año pasado', 'estadísticas'],
        plan: 'pago',
        pasos: [
          'En el menú, "Inteligencia" → "Reportes".',
          'Entrá a la pestaña "Top Vendidos".',
          'Elegí "Semana", "Mes", "Trimestre" o "Año", y cuántos querés ver en "Top N".',
          'La tabla muestra "Producto", "Unidades", "Facturación" y "Órdenes".',
          'Para comparar con el año pasado, entrá a "Comparativo YoY".',
        ],
      },
      {
        slug: 'como-sumar-un-usuario',
        titulo: 'Cómo sumar un usuario a tu equipo',
        descripcion: 'Darle acceso a un empleado o cajero con el rol que corresponde.',
        sinonimos: ['agregar empleado', 'sumar vendedor', 'crear usuario', 'dar acceso', 'roles', 'permisos'],
        plan: 'pago',
        pasos: [
          'En el menú, "Sistema" → "Usuarios".',
          'En "Crear usuario", completá "Nombre", "Email" y una "Contraseña temporal" (mínimo 6 caracteres).',
          'Elegí el "Rol": por ejemplo "Cajero", "Empleado" o "Gerente".',
          'Tocá "Crear usuario" y pasale la contraseña para su primer ingreso.',
          'Para sacarle el acceso, en "Usuarios registrados" tocá "Desactivar".',
        ],
      },
      {
        slug: 'como-pasarte-a-un-plan-pago',
        titulo: 'Cómo pasarte a un plan pago',
        descripcion: 'Desbloquear compras, canales, facturación y más usuarios desde el plan Gratis.',
        sinonimos: ['cambiar de plan', 'mejorar plan', 'suscribirme', 'pagar DashFlow', 'desbloquear funciones'],
        pasos: [
          'En el aviso de arriba ("Estás en el plan Gratis…"), tocá "Mejorar plan". También llegás tocando cualquier sección con candado.',
          'En "Elegí tu plan", mirá qué incluye cada uno.',
          'Si vas a facturar, en "Facturación electrónica (ARCA)" tocá "Elegir" en la cantidad de facturas que necesitás.',
          'Tocá "Suscribirme" en el plan que quieras y pagá con Mercado Pago.',
        ],
      },
    ],
  },
];

/* ---------------------------------------------------------------------------
 * Derivados
 * ------------------------------------------------------------------------- */

export interface LeccionNumerada extends Leccion {
  numero: number;
  moduloIndex: number;
}

export const LECCIONES: LeccionNumerada[] = MODULOS.flatMap((m, mi) => m.lecciones.map((l) => ({ ...l, moduloIndex: mi }))).map(
  (l, i) => ({ ...l, numero: i + 1 }),
);

const BY_SLUG = new Map(LECCIONES.map((l) => [l.slug, l]));
export const findLeccion = (slug: string) => BY_SLUG.get(slug);

/** Minutos totales de video (0 mientras no haya videos: el índice no los muestra). */
export const CURSO_MINUTOS = Math.round(LECCIONES.reduce((acc, l) => acc + (l.video?.duracion ?? 0), 0) / 60);

export const fmtDuracion = (seg: number) => `${Math.floor(seg / 60)}:${String(Math.round(seg % 60)).padStart(2, '0')}`;
export const isoDuracion = (seg: number) => `PT${Math.floor(seg / 60)}M${Math.round(seg % 60)}S`;

const normalizar = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Buscador del índice: todas las palabras tienen que aparecer en título, descripción o sinónimos. */
export const leccionCoincide = (l: Leccion, q: string) => {
  const hay = normalizar([l.titulo, l.descripcion, ...l.sinonimos].join(' '));
  return normalizar(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
};

/* ===========================================================================
 * GUION DE GRABACIÓN (fase 2)
 * ---------------------------------------------------------------------------
 * Grabar con Playwright contra un tenant demo en prod (dashflow.com.ar, entrando por
 * super-admin), ventana 1440x900, sin datos reales. Cada video < 1:20.
 *
 * Tenant demo: "Tienda Demo DashFlow" (subdominio sugerido: tienda-demo), rubro Indumentaria
 * + almacén. Hacen falta DOS tenants:
 *   A) en plan Gratis, para los módulos 1-4 y las lecciones 25-27 y 43 (se ve el aviso
 *      "Estás en el plan Gratis…" y los candados);
 *   B) en un plan pago (Pro o IA), para 17 y 28-42.
 * Datos demo (ficticios) a cargar antes de grabar, en el tenant que corresponda:
 *   - Categorías: "Indumentaria", "Calzado", "Accesorios", "Almacén".
 *   - Productos (con código de barras, PVP, Costo, Stock, mínimo y punto de reposición):
 *     "Remera básica algodón" REM-001 PVP 15000 costo 7000 stock 20 mín 5 repos 8;
 *     "Zapatilla urbana" ZAP-URB con variantes "talle 39 negro" 3, "talle 40 negro" 5, "talle 41 blanco" 2;
 *     "Gorra trucker" GOR-01 PVP 9000 costo 4000 stock 2 mín 3 repos 5 (sale ALERTA);
 *     "Yerba 1kg" YER-1K PVP 4500 costo 2800 stock 0 repos 10 (sale CRÍTICO);
 *     "Galletitas surtidas" GAL-01 PVP 1800; unos 15 más para que el Dashboard tenga volumen.
 *   - Un CSV de 5 productos para la lección de importar (encabezados del cuadro "Importar productos desde CSV").
 *   - Depósito extra "Local Centro" (Interno / Propio).
 *   - Clientes: "Almacén Los Andes SRL" CUIT 30-70000000-1, límite de crédito 200000;
 *     "Kiosco Don Pepe"; "Dietética La Esquina" CUIT 30-71234567-1 (2-3 ventas para facturar juntas).
 *   - Ventas: unas semanas de ventas de mostrador (para Dashboard, Reportes y Flujo de Caja), y
 *     2 ventas con saldo pendiente a "Almacén Los Andes SRL" (lecciones 26-27).
 *   - Proveedor: "Distribuidora Del Sur SRL" CUIT 30-71234567-8, plazo 30; una compra "OC-0001"
 *     (Yerba 1kg x 24 a 2800) pendiente de recibir y otra recibida con saldo (lección 34).
 *   - Gastos: "Nafta reparto" 25000; deuda "Imprenta – etiquetas" 80000 a "Gráfica Norte" a 15 días;
 *     fijos: Alquiler 450000 (día 10), Contador 80000 (día 5), Internet 25000 (día 15).
 *   - ARCA en Homologación con CUIT de prueba (lecciones 28-30). Sin eso, el switch "Facturar (CAE)"
 *     registra la venta sin factura.
 *   - Mercado Libre y Tienda Nube: cuentas de prueba (usuario test de ML) con 3-5 publicaciones
 *     ("Mate de calabaza", "Bombilla acero") y productos locales con el mismo SKU. Las tarjetas de
 *     canal las crea el backend: verificar que existan en el tenant demo.
 *   - Usuario a crear en la lección 42: "Martín Pérez", martin@tienda-demo.test, rol Cajero.
 *
 * Por lección (qué pantalla abrir → qué tocar, en orden):
 *  1 como-crear-tu-cuenta: /register (sin sesión) → completar con "Tienda Demo 2", mail de prueba
 *    eric.frick+df-curso@efcloud.tech → "Crear cuenta". Cortar al llegar a /completar-perfil.
 *  2 como-entrar-con-tu-whatsapp: /signup-wa → número de prueba → "Enviar código" → código → "Verificar y entrar".
 *    Necesita un número que reciba el OTP; si no, grabar solo hasta "Ingresá el código".
 *  3 como-completar-tu-perfil: cuenta recién creada → los 5 pasos (Indumentaria / 100 a 500 / Solo local físico,
 *    logo PNG genérico, paleta "Light — minimalista editorial") → "Ir al panel".
 *  4 como-recuperar-tu-contrasena: /login → mostrar campos → "¿Olvidaste tu contraseña?" → mail → "Enviar enlace".
 *  5 como-leer-el-dashboard: tenant A con ventas → recorrer tarjetas → "Período:" Semana/Mes → "Alertas de stock".
 *  6 como-cargar-los-datos-de-tu-empresa: Sistema → Configuración → "Datos del tenant" → cambiar nombre → "Guardar".
 *  7 como-cargar-un-producto: Productos → "Nuevo" → "Remera básica algodón" con los datos de arriba → "Crear".
 *  8 como-crear-categorias: "Nuevo" → engranaje "Gestionar categorías" → crear "Calzado" → "Cerrar".
 *  9 como-cargar-talles-y-colores: "Editar" en "Zapatilla urbana" → "Agregar variante" x3 → "Guardar".
 * 10 como-importar-productos-desde-excel: "Importar" → mostrar encabezados → elegir el CSV → "Importar" → resultado.
 * 11 como-actualizar-precios-en-masa: "Exportar" → (corte a Excel/Numbers cambiando price) → "Importar" → "Actualizados".
 * 12 como-calcular-tu-margen: Catálogo → Precios → elegir "Remera básica algodón" → tocar "PVP simulado" y "Comisión ML %".
 * 13 como-ajustar-el-stock: Inventario → lápiz en "Remera básica algodón" → 18, "Mercadería deteriorada" → "Guardar".
 * 14 como-crear-depositos: Inventario → "Depósitos" → "Local Centro" → "Crear" → "Por almacén".
 * 15 como-pasar-stock-entre-depositos: camión en "Remera básica algodón" → Principal → Local Centro, 5 → "Transferir".
 * 16 como-ver-el-valor-de-tu-inventario: tarjeta "Valor total del inventario" → "Costo"/"Venta" → "Ver lotes con stock".
 * 17 como-saber-que-reponer (tenant B): Reabastecimiento → "Solo alertas" → "Tabla" → "Exportar CSV".
 * 18 como-abrir-la-caja: Caja → "Monto inicial" 20000 → "Abrir caja".
 * 19 como-hacer-una-venta: Punto de Venta → escanear/buscar 3 productos (uno con variante) → Efectivo, "Paga con" → "Cobrar" → "Nueva venta".
 * 20 como-cobrar-con-varios-medios-de-pago: total 10000 → "Dividir" → 4000 Efectivo + "Resto" en Tarjeta → "Cobrar".
 * 21 como-aplicar-un-descuento: "Gestionar Extras" → Descuento, Porcentaje 10 → "Aplicar" → "Quitar".
 * 22 como-registrar-un-retiro-de-caja: Caja → "Egreso" 5000, Efectivo, "Pago proveedor de bolsas" → "Registrar".
 * 23 como-cerrar-la-caja: "Cerrar caja" → declarar 500 menos que lo esperado → "Cerrar caja" (se ve el faltante).
 * 24 como-buscar-y-eliminar-una-venta: Ventas → buscar → abrir detalle → "Editar" (mostrar "Revertir entrega") → "Eliminar venta" en una venta de prueba.
 * 25 como-cargar-un-cliente: Clientes → "Nuevo" → "Almacén Los Andes SRL" → "Guardar".
 * 26 como-registrar-un-pago: Ventas → venta con saldo → "Registrar pago" → parcial por Transferencia → "Registrar pago".
 * 27 como-cobrarle-a-un-cliente: Clientes → "Cuenta Corriente" de Almacén Los Andes → pestañas → "Registrar Pago".
 * 28 como-conectar-arca (tenant B, Homologación): Configuración → tarjeta ARCA → recorrer 3 pasos → "Guardar" → "Verificar conexión".
 * 29 como-facturar-en-el-punto-de-venta: POS → "Facturar (CAE)" → Factura B, Consumidor Final → "Cobrar".
 * 30 como-facturar-ventas-ya-hechas: Ventas → tildar 2 de "Dietética La Esquina" → "Facturar 2 ventas" → "Emitir comprobante" → "Descargar PDF".
 * 31 como-cargar-un-proveedor: Proveedores → "Nuevo proveedor" → "Distribuidora Del Sur SRL" → "Crear".
 * 32 como-registrar-una-compra: Compras → "Nueva compra" → OC-0001 → "Agregar fila" Yerba 1kg x 24 a 2800, IVA 21 → "Crear" → ícono copiar.
 * 33 como-recibir-la-mercaderia: Compras → "Recibir" en OC-0001 → 24 → "Confirmar recepción" → mostrar el stock en Inventario.
 * 34 como-pagarle-a-un-proveedor: Compras → "Registrar pago" → Transferencia → "Registrar pago" → Orden de pago.
 * 35 como-cargar-un-gasto: Gastos → "Cargar gasto" → "Nafta reparto" 25000 → "Guardar".
 * 36 como-cargar-tus-gastos-fijos: Gastos → "Fijos" → Alquiler 450000 día 10 → "Agregar".
 * 37 como-ver-el-flujo-de-caja: Flujo de Caja → último mes → "Consultar" → tarjetas y "Saldo Acumulado".
 * 38 como-conectar-mercado-libre: Canales → "Conectar MercadoLibre" → login con usuario test → volver → "Sincronizar".
 * 39 como-vincular-publicaciones-de-mercado-libre: Mapeo ML → vincular 3 publicaciones → "Guardar todos los mapeos".
 * 40 como-conectar-tienda-nube: Canales → "Conectar TiendaNube" → tienda de prueba → "Sincronizar".
 * 41 como-ver-tus-productos-mas-vendidos: Reportes → "Top Vendidos" → "Mes" → "Comparativo YoY".
 * 42 como-sumar-un-usuario: Usuarios → "Martín Pérez", Cajero → "Crear usuario" → "Desactivar".
 * 43 como-pasarte-a-un-plan-pago (tenant A): tocar "Mejorar plan" → "Elegí tu plan" → mostrar planes (no pagar).
 * ========================================================================= */
